import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadCommentImage } from '../api';
import type { CommentImage } from '../types';
import ImageThumbnail from './ImageThumbnail';

const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

interface CommentComposerProps {
  placeholder: string;
  onSubmit: (data: { content: string; imageUrls: string[] }) => Promise<void>;
}

export default function CommentComposer({ placeholder, onSubmit }: CommentComposerProps) {
  const { t } = useTranslation('common');
  const [content, setContent] = useState('');
  const [images, setImages] = useState<CommentImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const addFiles = async (files: File[]) => {
    // 上传中拦截按钮/粘贴/拖拽重入，避免第 4 张已传到 TOS 成孤儿
    if (uploading || files.length === 0) return;
    setError('');
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) { setError(t('commentComposer.tooMany')); return; }
    if (files.length > remaining) setError(t('commentComposer.tooMany'));
    const batch = files.slice(0, remaining);
    setUploading(true);
    try {
      for (const f of batch) {
        if (!ALLOWED_TYPES.includes(f.type)) { setError(t('commentComposer.badType')); continue; }
        if (f.size > MAX_IMAGE_BYTES) { setError(t('commentComposer.tooLarge')); continue; }
        try {
          const uploaded = await uploadCommentImage(f);
          setImages(prev => (prev.length < MAX_IMAGES ? [...prev, uploaded] : prev));
        } catch {
          setError(t('commentComposer.uploadFailed'));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length > 0) { e.preventDefault(); addFiles(files); }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeImage = (url: string) => setImages(prev => prev.filter(i => i.url !== url));

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!trimmed || submitting || uploading) return;
    setSubmitting(true);
    try {
      await onSubmit({ content: trimmed, imageUrls: images.map(i => i.url) });
      setContent('');
      setImages([]);
      setError('');
    } catch {
      setError(t('commentComposer.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  };

  const canAdd = images.length < MAX_IMAGES && !uploading;

  return (
    <div
      onPaste={onPaste}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      {error && <div style={{ color: 'var(--status-failed, #ef4444)', fontSize: '0.78rem' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={2000}
          rows={2}
          style={{ flex: 1, padding: '0.5rem 0.75rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text)', fontSize: '0.88rem', outline: 'none', resize: 'vertical', maxHeight: 160, fontFamily: 'inherit' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {images.map(img => (
          <div key={img.url} style={{ position: 'relative' }}>
            <ImageThumbnail url={img.url} fileName={img.fileName} thumbSize={64} />
            <button
              type="button"
              aria-label={t('commentComposer.removeImage')}
              onClick={() => removeImage(img.url)}
              style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, lineHeight: 1, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => fileRef.current?.click()}
            style={{
              width: 64, height: 64, padding: 0,
              border: '1px dashed var(--border)', borderRadius: 4,
              background: 'var(--surface-2)', color: 'var(--text-3)',
              fontSize: '0.75rem', lineHeight: 1.3, cursor: canAdd ? 'pointer' : 'default',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {uploading ? t('commentComposer.uploading') : (
              <>
                <span style={{ fontSize: '1.1rem' }}>＋</span>
                <span>{t('commentComposer.addImage')}</span>
              </>
            )}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          onChange={onPickFiles}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!content.trim() || submitting || uploading}
          style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', marginLeft: 'auto', alignSelf: 'flex-end' }}
        >
          {submitting ? t('commentComposer.sending') : t('commentComposer.send')}
        </button>
      </div>
    </div>
  );
}
